/* eslint-disable @typescript-eslint/ban-ts-comment */
import { BriefData } from '@/entities/ihamkor/types';
import { Box, Button, Grid, Stack, StackProps, styled, Typography } from '@mui/material';
import { FC } from 'react';
import classes from './subject-general-info.module.sass';
import { Trans, useTranslation } from 'react-i18next';
import { UITable } from '@/shared/ui/ui-table';
import { triada } from '@/app/helpers';
import ComponentWithLoader from '@/shared/ComponentWithLoader/ComponentWithLoader';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import IhamkorLogoIcon from '@/app/assets/icons/ihamkor-logo.svg?react';
import { UIDefinitionProps } from '@/shared/ui/ui-definition-list/types';
import { UIDefinitionList } from '@/shared/ui/ui-definition-list';
import { getBriefDataFields } from './lib';
import _ from 'lodash';
import WarningIcon from '@/app/assets/icons/warning.svg?react';

interface Props {
  onClick: () => void;
  refetch: () => void;
  data?: BriefData;
  isLoading?: boolean;
  error?: unknown;
  emptyTitle?: string;
}

const DefinitionList = styled((props: UIDefinitionProps) => <UIDefinitionList {...props} />)();

const StackComp: FC<StackProps> = (props) => (
  <Stack
    direction='row'
    spacing={2}
    // @ts-ignore
    sx={{
      '& > *': {
        flex: 1,
        ...props.sx,
      },
    }}
    className={classes['row']}
    useFlexGap
    flexWrap='wrap'
    {...props}
  />
);

export const CompanyBriefData: FC<Props> = ({
  data,
  onClick,
  isLoading,
  error,
  refetch,
  emptyTitle,
}) => {
  const { t } = useTranslation();

  const getRating = (item: BriefData['sustainabilityRating']) => {
    if (Object.values(item).some((el) => el == null)) {
      return '-';
    }
    let ratingColor = 'inherit';

    if (item.type) {
      switch (item.type[0]) {
        case 'A':
          ratingColor = '#00B050';
          break;
        case 'B':
          ratingColor = '#0070C0';
          break;
        case 'C':
          ratingColor = '#7030A0';
          break;
        case 'D':
          ratingColor = '#FF0000';
          break;
        default:
          break;
      }
    }

    return (
      <>
        {item.type} - {item.points || 0}{' '}
        <Box component='span' color={ratingColor}>
          {item.title}
        </Box>
      </>
    );
  };

  const isError = (data && _.isEmpty(data)) || error;

  const fields = getBriefDataFields(t, data);

  return (
    <ComponentWithLoader isLoading={isLoading}>
      {isError && !isLoading ? (
        <EmptyDataPlaceholder
          isEmpty
          title={t('ihamkor.error_title')}
          subtitle={t('ihamkor.error_subtitle')}
          icon={<WarningIcon />}
          endAdornment={
            <Button color='inherit' onClick={refetch}>
              {t('ihamkor.try_again')}
            </Button>
          }
          sx={{
            '& .empty-data-placeholder-icon': {
              bgcolor: 'rgba(243, 79, 79, 0.04)',
            },
          }}
        />
      ) : data ? (
        <Box id='subject-short-info-contaner'>
          <StackComp mb={3} component='div'>
            <DefinitionList
              term={t('ihamkor.subject_name')}
              description={data?.shortName}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <DefinitionList
              term={t('ihamkor.tin')}
              description={data?.tin}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <DefinitionList
              term={t('ihamkor.registration_date')}
              description={data?.registrationDate}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <DefinitionList
              term={t('ihamkor.sustainability_rating')}
              description={getRating(data?.sustainabilityRating)}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
          </StackComp>

          <Typography color='text.secondary' fontSize='1.4rem' mt={4} fontWeight={500}>
            {t('ihamkor.founders')}:
          </Typography>
          <UITable
            sx={{
              mt: 1,
              mb: 4,
              '& .MuiTableCell-root': {
                px: 1.5,
                py: 1,
                textTransform: 'uppercase',
              },
              '& .MuiTableHead-root': {
                backgroundColor: 'rgba(13, 29, 88, 0.04)',
                '& .MuiTableCell-root': {
                  color: 'text.secondary',
                },
              },
            }}
            data={
              data?.isJointStockCompany && !data?.founders?.length
                ? ([
                    {
                      fullName: t('ihamkor.shareholders'),
                      tin: '',
                      pinfl: '',
                      otherLegalEntities: [],
                      share: '100',
                      subjectType: t('ihamkor.shareholder'),
                      isLegalEntity: true,
                    },
                  ] as BriefData['founders'])
                : data?.founders
            }
            columns={[
              {
                label: t('ihamkor.fio_or_name'),
                key: 'fullName',
                sx: {
                  width: '33%',
                },
              },
              {
                label: t('ihamkor.who_is'),
                key: 'subjectType',
                sx: {
                  width: '17%',
                  color: 'text.secondary',
                },
              },
              {
                label: t('ihamkor.proportion'),
                render: ({ share }) => triada(share, { decimal: 2 }) + ' %',
                align: 'right',
                headerAlign: 'right',
                sx: {
                  width: '17%',
                },
              },
              {
                label: t('ihamkor.participation_in_other_subjects'),
                // hidden: !!data?.isJointStockCompany,
                render: (row) => {
                  return (
                    <Stack spacing={1} useFlexGap flexWrap='wrap'>
                      {row.otherLegalEntities.map((el, idx) => {
                        return (
                          <Box
                            key={idx}
                            sx={{
                              '&:hover': {},
                            }}
                          >
                            {el.name}
                            {idx !== row.otherLegalEntities.length - 1 ? ', ' : ''}
                          </Box>
                        );
                      })}
                    </Stack>
                  );
                },
              },
            ]}
            EmptyPlaceholder={t('ihamkor.no_info')}
          />

          <Grid container rowSpacing={[2, 2, 4]} columnSpacing={2}>
            {fields.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <UIDefinitionList
                  term={t(`ihamkor.${item.termKey}`)}
                  description={item.value ? item.value : t('ihamkor.no_info')}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <EmptyDataPlaceholder
          isEmpty
          title={emptyTitle || t('ihamkor_empty_data_title_supplier')}
          icon={<IhamkorLogoIcon />}
          subtitle={
            <Trans
              i18nKey='ihamkor_empty_data_subtitle'
              components={[<a href='https://ihamkor.uz' target='_blank' />]}
            />
          }
          endAdornment={
            <Button color='inherit' onClick={onClick}>
              {t('get_data')}
            </Button>
          }
        />
      )}
    </ComponentWithLoader>
  );
};
